import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Shield, 
  Plus, 
  Edit, 
  Trash2,
  Loader2, 
  Users,
  CheckCircle,
  XCircle,
  Lock,
  Unlock,
  Copy,
  AlertCircle,
  Search,
  Settings,
  Eye,
  FileText,
  MessageSquare,
  Calendar,
  BarChart3,
  UserCog,
  Briefcase
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  navigation: Eye,
  participants: Users,
  enrollments: Briefcase,
  cases: FileText,
  case_notes: FileText,
  documents: FileText,
  communications: MessageSquare,
  tasks_appointments: Calendar,
  assessments: FileText,
  reports: BarChart3,
  contracts: FileText,
  administration: Settings,
  ai_features: Settings
};

const RolesPermissionsPage = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState({ categories: {}, all_permissions: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('roles');
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [deletingRole, setDeletingRole] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [roleForm, setRoleForm] = useState({
    name: '',
    display_name: '',
    description: '',
    permissions: [],
    color: '#6b7280',
    is_active: true
  });

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/roles`);
      setRoles(response.data.roles || response.data || []);
    } catch (error) {
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await axios.get(`${API}/permissions`);
      setPermissions(response.data);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
    }
  };

  const handleOpenRoleDialog = (role = null) => {
    if (role) {
      setEditingRole(role);
      setRoleForm({
        name: role.name || '',
        display_name: role.display_name || '',
        description: role.description || '',
        permissions: role.permissions || [],
        color: role.color || '#6b7280',
        is_active: role.is_active !== false
      });
    } else {
      setEditingRole(null);
      setRoleForm({
        name: '',
        display_name: '',
        description: '',
        permissions: [],
        color: '#6b7280',
        is_active: true
      });
    }
    setShowRoleDialog(true);
  };

  const handleDuplicateRole = (role) => {
    setEditingRole(null);
    setRoleForm({
      name: '',
      display_name: `${role.display_name} (Copy)`,
      description: role.description || '',
      permissions: [...(role.permissions || [])],
      color: role.color || '#6b7280',
      is_active: true
    });
    setShowRoleDialog(true);
  };

  const handleSaveRole = async (e) => {
    e.preventDefault();
    
    if (!roleForm.display_name) {
      toast.error('Role name is required');
      return;
    }
    
    setSaving(true);

    try {
      if (editingRole) {
        await axios.put(`${API}/roles/${editingRole.id || editingRole.name}`, {
          display_name: roleForm.display_name,
          description: roleForm.description,
          permissions: roleForm.permissions,
          color: roleForm.color,
          is_active: roleForm.is_active
        });
        toast.success('Role updated successfully');
      } else {
        const name = roleForm.name || roleForm.display_name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
        await axios.post(`${API}/roles`, {
          name,
          display_name: roleForm.display_name,
          description: roleForm.description,
          permissions: roleForm.permissions,
          color: roleForm.color,
          is_active: roleForm.is_active
        });
        toast.success('Role created successfully');
      }
      setShowRoleDialog(false);
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!deletingRole) return;
    setSaving(true);

    try {
      await axios.delete(`${API}/roles/${deletingRole.id || deletingRole.name}`);
      toast.success('Role deleted successfully');
      setShowDeleteDialog(false);
      setDeletingRole(null);
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete role');
    } finally {
      setSaving(false);
    }
  };

  const togglePermission = (permissionId) => {
    setRoleForm(prev => {
      const newPermissions = prev.permissions.includes(permissionId)
        ? prev.permissions.filter(p => p !== permissionId)
        : [...prev.permissions, permissionId];
      return { ...prev, permissions: newPermissions };
    });
  };

  const toggleCategoryPermissions = (categoryPermissions) => {
    const permIds = categoryPermissions.map(p => p.id);
    const allSelected = permIds.every(id => roleForm.permissions.includes(id));
    
    setRoleForm(prev => {
      if (allSelected) {
        return { ...prev, permissions: prev.permissions.filter(p => !permIds.includes(p)) };
      } else {
        const newPerms = [...new Set([...prev.permissions, ...permIds])];
        return { ...prev, permissions: newPerms };
      }
    });
  };

  const selectAllPermissions = () => {
    setRoleForm(prev => ({
      ...prev,
      permissions: permissions.all_permissions || []
    }));
  };

  const clearAllPermissions = () => {
    setRoleForm(prev => ({
      ...prev,
      permissions: []
    }));
  };

  const filteredRoles = roles.filter(role =>
    role.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const systemRoles = filteredRoles.filter(r => r.is_system_role);
  const customRoles = filteredRoles.filter(r => !r.is_system_role);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="roles-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="roles-permissions-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-foreground">Roles & Permissions</h1>
            <p className="text-muted-foreground">Manage user roles and access permissions</p>
          </div>
        </div>
        <Button onClick={() => handleOpenRoleDialog()} data-testid="add-role-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Custom Role
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="roles" data-testid="roles-tab">
            <Shield className="w-4 h-4 mr-2" />
            Roles ({roles.length})
          </TabsTrigger>
          <TabsTrigger value="matrix" data-testid="matrix-tab">
            <Settings className="w-4 h-4 mr-2" />
            Permission Matrix
          </TabsTrigger>
        </TabsList>

        <TabsContent value="roles" className="space-y-6">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search roles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="role-search-input"
              />
            </div>
          </div>

          {/* System Roles */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-amber-500" />
                System Roles ({systemRoles.length})
              </CardTitle>
              <CardDescription>
                Predefined roles that cannot be deleted. Permissions can be customized.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {systemRoles.map((role) => (
                  <Card key={role.id || role.name} className="relative" data-testid={`role-card-${role.name}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: role.color || '#6b7280' }}
                          />
                          <CardTitle className="text-base">{role.display_name}</CardTitle>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          <Lock className="w-3 h-3 mr-1" />
                          System
                        </Badge>
                      </div>
                      <CardDescription className="text-xs line-clamp-2">
                        {role.description || 'No description'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-2">
                      <div className="flex items-center justify-between text-sm text-muted-foreground mb-3">
                        <span>{role.permissions?.length || 0} permissions</span>
                        <Badge variant={role.is_active !== false ? "default" : "secondary"}>
                          {role.is_active !== false ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1"
                          onClick={() => handleOpenRoleDialog(role)}
                        >
                          <Edit className="w-3 h-3 mr-1" />
                          Edit
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDuplicateRole(role)}
                          title="Duplicate as custom role"
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Custom Roles */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Unlock className="w-5 h-5 text-green-500" />
                Custom Roles ({customRoles.length})
              </CardTitle>
              <CardDescription>
                User-created roles with full customization.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {customRoles.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No custom roles created yet</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => handleOpenRoleDialog()}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create First Custom Role
                  </Button>
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {customRoles.map((role) => (
                    <Card key={role.id || role.name} className="relative" data-testid={`role-card-${role.name}`}>
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: role.color || '#6b7280' }}
                            />
                            <CardTitle className="text-base">{role.display_name}</CardTitle>
                          </div>
                          <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                            <Unlock className="w-3 h-3 mr-1" />
                            Custom
                          </Badge>
                        </div>
                        <CardDescription className="text-xs line-clamp-2">
                          {role.description || 'No description'}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="pt-2">
                        <div className="flex items-center justify-between text-sm text-muted-foreground mb-3">
                          <span>{role.permissions?.length || 0} permissions</span>
                          <Badge variant={role.is_active !== false ? "default" : "secondary"}>
                            {role.is_active !== false ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="flex-1"
                            onClick={() => handleOpenRoleDialog(role)}
                          >
                            <Edit className="w-3 h-3 mr-1" />
                            Edit
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDuplicateRole(role)}
                            title="Duplicate"
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => {
                              setDeletingRole(role);
                              setShowDeleteDialog(true);
                            }}
                            title="Delete"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="matrix" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Permission Matrix</CardTitle>
              <CardDescription>
                Overview of permissions across all roles
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="sticky left-0 bg-background z-10 min-w-[200px]">Permission</TableHead>
                      {roles.map((role) => (
                        <TableHead key={role.name} className="text-center min-w-[100px]">
                          <div className="flex flex-col items-center gap-1">
                            <div 
                              className="w-2 h-2 rounded-full" 
                              style={{ backgroundColor: role.color || '#6b7280' }}
                            />
                            <span className="text-xs">{role.display_name}</span>
                          </div>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(permissions.categories || {}).map(([categoryKey, category]) => (
                      <React.Fragment key={categoryKey}>
                        <TableRow className="bg-muted/50">
                          <TableCell colSpan={roles.length + 1} className="font-semibold">
                            {category.label}
                          </TableCell>
                        </TableRow>
                        {category.permissions?.map((perm) => (
                          <TableRow key={perm.id}>
                            <TableCell className="sticky left-0 bg-background">
                              <div>
                                <span className="font-medium text-sm">{perm.label}</span>
                                <p className="text-xs text-muted-foreground">{perm.description}</p>
                              </div>
                            </TableCell>
                            {roles.map((role) => (
                              <TableCell key={`${role.name}-${perm.id}`} className="text-center">
                                {role.permissions?.includes(perm.id) ? (
                                  <CheckCircle className="w-4 h-4 text-green-500 mx-auto" />
                                ) : (
                                  <XCircle className="w-4 h-4 text-gray-300 mx-auto" />
                                )}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Role Dialog */}
      <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              {editingRole ? `Edit Role: ${editingRole.display_name}` : 'Create Custom Role'}
            </DialogTitle>
            <DialogDescription>
              {editingRole?.is_system_role 
                ? 'Customize permissions for this system role. The role cannot be deleted.'
                : 'Define a new role with specific permissions.'
              }
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSaveRole}>
            <ScrollArea className="max-h-[60vh] pr-4">
              <div className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="display_name">Role Name *</Label>
                    <Input
                      id="display_name"
                      value={roleForm.display_name}
                      onChange={(e) => setRoleForm({...roleForm, display_name: e.target.value})}
                      placeholder="e.g., Senior Case Manager"
                      required
                      data-testid="role-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="color">Color</Label>
                    <div className="flex gap-2">
                      <Input
                        id="color"
                        type="color"
                        value={roleForm.color}
                        onChange={(e) => setRoleForm({...roleForm, color: e.target.value})}
                        className="w-16 h-10 p-1"
                      />
                      <Input
                        value={roleForm.color}
                        onChange={(e) => setRoleForm({...roleForm, color: e.target.value})}
                        placeholder="#6b7280"
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={roleForm.description}
                    onChange={(e) => setRoleForm({...roleForm, description: e.target.value})}
                    placeholder="Describe this role's purpose and responsibilities"
                    rows={2}
                    data-testid="role-description-input"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    checked={roleForm.is_active}
                    onCheckedChange={(checked) => setRoleForm({...roleForm, is_active: checked})}
                    data-testid="role-active-switch"
                  />
                  <Label>Active Role</Label>
                </div>

                {/* Permissions */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold">Permissions</Label>
                    <div className="flex gap-2">
                      <Button type="button" variant="outline" size="sm" onClick={selectAllPermissions}>
                        Select All
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={clearAllPermissions}>
                        Clear All
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Selected: {roleForm.permissions.length} of {permissions.all_permissions?.length || 0} permissions
                  </p>

                  {Object.keys(permissions.categories || {}).length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                      Loading permissions...
                    </div>
                  ) : (
                    <Accordion type="multiple" className="w-full">
                      {Object.entries(permissions.categories || {}).map(([categoryKey, category]) => {
                        const CategoryIcon = CATEGORY_ICONS[categoryKey] || Settings;
                        const categoryPerms = category.permissions || [];
                        const selectedCount = categoryPerms.filter(p => roleForm.permissions.includes(p.id)).length;
                        const allSelected = categoryPerms.length > 0 && selectedCount === categoryPerms.length;
                        
                        return (
                          <AccordionItem key={categoryKey} value={categoryKey} className="border rounded-lg mb-2 px-4">
                            <AccordionTrigger className="hover:no-underline py-3">
                              <div className="flex items-center gap-3">
                                <CategoryIcon className="w-4 h-4 text-muted-foreground" />
                                <span className="font-medium">{category.label}</span>
                                <Badge variant={selectedCount > 0 ? "default" : "outline"} className="ml-2">
                                  {selectedCount}/{categoryPerms.length}
                                </Badge>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent>
                              <div className="space-y-3 pt-2 pb-4">
                                <div className="flex items-center gap-2 pb-2 border-b">
                                  <Checkbox
                                    checked={allSelected}
                                    onCheckedChange={() => toggleCategoryPermissions(categoryPerms)}
                                  />
                                  <Label className="text-sm font-medium cursor-pointer" onClick={() => toggleCategoryPermissions(categoryPerms)}>
                                    Select all in {category.label}
                                  </Label>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                  {categoryPerms.map((perm) => (
                                    <div 
                                      key={perm.id} 
                                      className={`flex items-start gap-2 p-2 rounded border cursor-pointer transition-colors ${
                                        roleForm.permissions.includes(perm.id) 
                                          ? 'bg-primary/10 border-primary' 
                                          : 'hover:bg-muted/50 border-transparent'
                                      }`}
                                      onClick={() => togglePermission(perm.id)}
                                    >
                                      <Checkbox
                                        checked={roleForm.permissions.includes(perm.id)}
                                        onCheckedChange={() => togglePermission(perm.id)}
                                        className="mt-0.5"
                                      />
                                      <div>
                                        <Label className="text-sm cursor-pointer font-medium">{perm.label}</Label>
                                        <p className="text-xs text-muted-foreground">{perm.description}</p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        );
                      })}
                    </Accordion>
                  )}
                </div>
              </div>
            </ScrollArea>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setShowRoleDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-role-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingRole ? 'Update Role' : 'Create Role'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Delete Role
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the role &quot;{deletingRole?.display_name}&quot;? 
              Users assigned to this role will need to be reassigned.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteRole} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RolesPermissionsPage;
